import { Car, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useTheme } from "@/context/ThemeContext";
import { useLanguage } from "@/context/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useShowNavigateBack } from "@/hooks/useShowNavigateBack";

import BtnNavigateBack from "@/components/ui/btn_navigate_back";
import { MyAuctionsView } from "@/components/my-auctions/MyAuctionsView";
import { ContractSignatureSuccessDialog } from "@/components/orders/ContractSignatureSuccessDialog";
import { ContractSignatureFailureDialog } from "@/components/orders/ContractSignatureFailureDialog";
import { useSellerContractSignatureReturn } from "@/hooks/useSellerContractSignatureReturn";
import {
  ColoredOutlineButton,
  OUTLINE_ICON_BUTTON_ICON_CLASS,
  OUTLINE_TEXT_BUTTON_ICON_CLASS,
} from "@/components/ui/colored-outline-button";
import { MobilePillTabs } from "@/components/ui/mobile-pill-tabs";
import { useNavigate } from "react-router-dom";
import { auctionsLabels } from "@/i18n";
import { ListingsOrdersTabs } from "@/components/dashboard/ListingsOrdersTabs";
import { cn } from "@/lib/utils";
import {
  markSellerAttentionSeen,
  useMyObjectsAttention,
} from "@/hooks/useMyObjectsAttention";

const MyAuctions = () => {
  const [currentView, setCurrentView] = useState<"active" | "completed">(
    "active",
  );
  const { theme } = useTheme();
  const { language } = useLanguage();
  const m = auctionsLabels(language).myAuctions;
  const isDark = theme === "dark";
  const isMobile = useIsMobile();
  const { isNative } = useNativePlatform();
  const showPageBack = useShowNavigateBack();
  const navigate = useNavigate();
  const signatureReturn = useSellerContractSignatureReturn();
  const scLabels = (m.sellerContract ?? {}) as Record<string, string>;
  const { sellerNeedsAction, pendingSellerContractIds } =
    useMyObjectsAttention();

  useEffect(() => {
    if (currentView === "completed" && pendingSellerContractIds.length > 0) {
      markSellerAttentionSeen(pendingSellerContractIds);
    }
  }, [currentView, pendingSellerContractIds]);

  const renderContent = () => {
    switch (currentView) {
      case "active":
        return <MyAuctionsView type="active" />;
      case "completed":
        return <MyAuctionsView type="completed" />;
      default:
        return <MyAuctionsView type="active" />;
    }
  };

  const handlePostAnAd = () => {
    navigate("/car-post-an-ad");
  };

  return (
    <div
      className={cn(
        "container mx-auto px-4 py-2 relative z-10",
        isNative && "native-my-auctions-page",
      )}
    >
      {showPageBack ? (
        <div className="mb-3">
          <BtnNavigateBack />
        </div>
      ) : null}

      <ListingsOrdersTabs active="my-auctions" />

      <div className="flex flex-col md:flex-row gap-4">
        {!isMobile && (
          <div className="md:w-64 flex-shrink-0">
            <h2
              className={`text-2xl font-bold ${
                isDark ? "text-accent" : "text-accent"
              } mb-4`}
            >
              {m.pageTitle}
            </h2>
            <nav className="flex flex-col space-y-2">
              <button
                onClick={() => setCurrentView("active")}
                className={`text-left py-2 px-4 rounded-md transition-colors ${
                  currentView === "active"
                    ? isDark
                      ? "text-white bg-secondary border-l-2 border-accent"
                      : "text-black bg-secondary border-l-2 border-accent"
                    : "hover:bg-secondary/50"
                }`}
              >
                {m.tabActive}
              </button>
              <button
                onClick={() => setCurrentView("completed")}
                className={`relative text-left py-2 px-4 rounded-md transition-colors ${
                  currentView === "completed"
                    ? isDark
                      ? "text-white bg-secondary border-l-2 border-accent"
                      : "text-black bg-secondary border-l-2 border-accent"
                    : "hover:bg-secondary/50"
                }`}
              >
                {m.tabCompleted}
                {sellerNeedsAction ? (
                  <span
                    className="absolute top-2.5 right-3 h-2 w-2 rounded-full bg-red-500"
                    aria-hidden
                  />
                ) : null}
              </button>
            </nav>
            <ColoredOutlineButton
              type="button"
              variant="success"
              tintedBackground
              size="sm"
              onClick={handlePostAnAd}
              className="mt-4 h-9 px-3 text-sm gap-1.5 w-full"
            >
              <Car
                className={OUTLINE_TEXT_BUTTON_ICON_CLASS}
                strokeWidth={2.35}
              />
              <span>{m.createAd}</span>
            </ColoredOutlineButton>
          </div>
        )}

        <div className="flex-1 animate-fade-in min-w-0">
          {isMobile ? (
            <div className="flex items-center gap-2 mb-3">
              <MobilePillTabs
                equalWidth
                className="mb-0 flex-1 min-w-0"
                items={[
                  { value: "active", label: m.tabActiveShort },
                  {
                    value: "completed",
                    label: (
                      <span className="relative inline-flex items-center gap-1.5">
                        {m.tabCompletedShort}
                        {sellerNeedsAction ? (
                          <span
                            className="h-1.5 w-1.5 rounded-full bg-red-500"
                            aria-hidden
                          />
                        ) : null}
                      </span>
                    ),
                  },
                ]}
                value={currentView}
                onChange={setCurrentView}
              />
              <ColoredOutlineButton
                type="button"
                variant="success"
                tintedBackground
                size="icon"
                onClick={handlePostAnAd}
                aria-label={m.createAd}
                title={m.createAd}
                className="shrink-0"
              >
                <Plus
                  className={OUTLINE_ICON_BUTTON_ICON_CLASS}
                  strokeWidth={2.35}
                />
              </ColoredOutlineButton>
            </div>
          ) : null}
          {renderContent()}
        </div>
      </div>

      <ContractSignatureSuccessDialog
        isOpen={signatureReturn.showSuccess}
        onClose={signatureReturn.closeSuccess}
        title={scLabels.signatureSuccessTitle}
        description={scLabels.signatureSuccessDescription}
      />
      <ContractSignatureFailureDialog
        isOpen={signatureReturn.showFailure}
        onClose={signatureReturn.closeFailure}
        title={scLabels.signatureFailureTitle}
        description={scLabels.signatureFailureDescription}
      />
    </div>
  );
};

export default MyAuctions;
